import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import BottomSheet from "components/common/BottomSheet";
import { useSnackbar } from "contexts/SnackbarContext";
import {
    PB_DISCIPLINES,
    isSecondsDiscipline,
    getBestPb,
    createPb,
} from "api/freedivingPb";

const todayStr = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const MM_SS_REGEX = /^([0-9]{1,3}):([0-5][0-9])$/;

const initialForm = () => ({
    discipline: "CWT",
    value_meters: "",
    value_seconds: "",
    recorded_at: todayStr(),
    note: "",
});

/**
 * 강사용 PB 입력 시트.
 * @param {boolean} isOpen
 * @param {function} onClose
 * @param {string} userId - 수강생 user_id (이메일 식별자, PB API 계약 유지)
 * @param {string} userName
 * @param {function} onSaved - 저장 후 콜백 (best 재조회 트리거)
 * @param {string} [historyUserKey] - 상세 이력 URL용 공개 식별자(public_id)
 */
const PbInputSheet = ({ isOpen, onClose, userId, userName, onSaved, historyUserKey }) => {
    const navigate = useNavigate();
    const { showSuccess, showError } = useSnackbar();
    const [bestRecords, setBestRecords] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [form, setForm] = useState(initialForm);

    const loadBest = useCallback(async () => {
        if (!userId) return;
        const list = await getBestPb(userId);
        setBestRecords(Array.isArray(list) ? list : []);
    }, [userId]);

    useEffect(() => {
        if (isOpen) {
            setForm(initialForm());
            loadBest();
        }
    }, [isOpen, loadBest]);

    const bestByCode = new Map(bestRecords.map((r) => [r.discipline, r]));

    const isSeconds = isSecondsDiscipline(form.discipline);

    const updateField = (key, val) => setForm((p) => ({ ...p, [key]: val }));

    const handleSubmit = async () => {
        if (!userId || isSaving) return;

        if (isSeconds) {
            if (!form.value_seconds || !MM_SS_REGEX.test(form.value_seconds)) {
                showError("STA 기록은 mm:ss 형식(예: 06:30)으로 입력해주세요");
                return;
            }
        } else {
            const num = Number(form.value_meters);
            if (!form.value_meters || isNaN(num) || num <= 0) {
                showError("거리를 입력해주세요");
                return;
            }
            if (num > 999.9) {
                showError("거리는 최대 999.9m까지 입력 가능합니다");
                return;
            }
        }
        if (!form.recorded_at) {
            showError("측정일을 입력해주세요");
            return;
        }

        const payload = {
            user: userId,
            discipline: form.discipline,
            recorded_at: form.recorded_at,
            note: form.note || "",
        };
        if (isSeconds) {
            payload.value_seconds = form.value_seconds;
            payload.value_meters = null;
        } else {
            payload.value_meters = Number(form.value_meters).toFixed(1);
            payload.value_seconds = null;
        }

        try {
            setIsSaving(true);
            await createPb(payload);
            showSuccess("PB 기록을 저장했습니다");
            setForm(initialForm());
            await loadBest();
            onSaved?.();
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                err?.response?.data?.error ||
                "PB 기록 저장에 실패했습니다";
            showError(typeof msg === "string" ? msg : "PB 기록 저장에 실패했습니다");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <BottomSheet
            isOpen={isOpen}
            onClose={onClose}
            title={`${userName || "수강생"} PB 입력`}
            height="auto"
            className="pb-input-sheet"
        >
            <div className="pb-best-summary">
                <div className="pb-best-summary__header">
                    <p className="pb-best-summary__title">현재 최고 기록</p>
                    {historyUserKey && (
                        <button
                            type="button"
                            className="pb-best-summary__history-btn"
                            onClick={() => {
                                onClose?.();
                                navigate(`/pb/history/${encodeURIComponent(historyUserKey)}`);
                            }}
                        >
                            상세 이력 보기
                        </button>
                    )}
                </div>
                <div className="pb-best-summary__grid">
                    {PB_DISCIPLINES.map((d) => {
                        const rec = bestByCode.get(d.code);
                        const unitSuffix = d.type === "seconds" ? "mm:ss" : "m";
                        return (
                            <div key={d.code} className="pb-best-summary__item">
                                <span className="pb-best-summary__label">{d.label}</span>
                                <span className="pb-best-summary__value">
                                    {rec?.value_display || `- ${unitSuffix}`}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="pb-input-form">
                <div className="pb-input-row">
                    <label className="pb-input-label">종목</label>
                    <select
                        className="pb-input-select"
                        value={form.discipline}
                        onChange={(e) => updateField("discipline", e.target.value)}
                    >
                        {PB_DISCIPLINES.map((d) => (
                            <option key={d.code} value={d.code}>
                                {d.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="pb-input-row">
                    <label className="pb-input-label">
                        기록 {isSeconds ? "(mm:ss)" : "(m)"}
                    </label>
                    {isSeconds ? (
                        <input
                            className="pb-input-text"
                            type="text"
                            placeholder="06:30"
                            value={form.value_seconds}
                            onChange={(e) => updateField("value_seconds", e.target.value)}
                        />
                    ) : (
                        <input
                            className="pb-input-text"
                            type="number"
                            step="0.1"
                            min="0"
                            max="999.9"
                            placeholder="35.5"
                            value={form.value_meters}
                            onChange={(e) => updateField("value_meters", e.target.value)}
                        />
                    )}
                </div>

                <div className="pb-input-row">
                    <label className="pb-input-label">측정일</label>
                    <input
                        className="pb-input-text"
                        type="date"
                        value={form.recorded_at}
                        onChange={(e) => updateField("recorded_at", e.target.value)}
                    />
                </div>

                <div className="pb-input-row">
                    <label className="pb-input-label">메모 (선택)</label>
                    <input
                        className="pb-input-text"
                        type="text"
                        placeholder="예) 풀 환경, 컨디션 등"
                        value={form.note}
                        onChange={(e) => updateField("note", e.target.value)}
                    />
                </div>

                <div className="pb-input-actions">
                    <button
                        type="button"
                        className="btn btn-purple500 btn-fit"
                        onClick={handleSubmit}
                        disabled={isSaving}
                    >
                        {isSaving ? "저장 중..." : "저장"}
                    </button>
                </div>
            </div>
        </BottomSheet>
    );
};

export default PbInputSheet;
